(() => {
  const roots = document.querySelectorAll('[data-nsl-launch]');
  if (!roots.length) return;

  document.body.classList.add('nsl-landing-active');

  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const currency = window.Shopify?.currency?.active || 'BRL';
  const locale = (document.documentElement.lang || 'pt-BR').replace('_', '-');
  let moneyFormatter;
  try {
    moneyFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
  } catch (error) {
    moneyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  const money = (cents) => moneyFormatter.format((Number(cents) || 0) / 100);

  const cartFetch = async (path, body) => {
    const options = { headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const response = await fetch(`${shopRoot()}${path}`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Não foi possível atualizar o carrinho.');
    return data;
  };

  roots.forEach((root) => {
    const sticky = root.querySelector('[data-nsl-sticky]');
    const hero = root.querySelector('[data-nsl-hero]');
    const buyTarget = root.querySelector('#nsl-buy-' + root.dataset.sectionId);
    const productImages = [...root.querySelectorAll('[data-nsl-product-image]')];
    const colorName = root.querySelector('[data-nsl-color-name]');
    const priceNodes = [...root.querySelectorAll('[data-nsl-price]')];
    const swatches = [...root.querySelectorAll('[data-nsl-swatch]')];
    const message = root.querySelector('[data-nsl-message]');
    const parallaxNodes = [...root.querySelectorAll('[data-nsl-par]')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeVariantId = root.dataset.variantId || '';
    let ticking = false;

    /* ---------------------------------------------------------------- scroll */

    const updateParallax = () => {
      if (reduceMotion || !parallaxNodes.length) return;
      const vh = window.innerHeight || 1;
      parallaxNodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.bottom < -vh * 0.4 || rect.top > vh * 1.4) return;
        const center = rect.top + rect.height / 2;
        const p = Math.max(-1, Math.min(1, 1 - (2 * center) / vh));
        node.style.setProperty('--p', p.toFixed(4));
      });
    };

    const updateScroll = () => {
      ticking = false;
      updateParallax();
      if (sticky && hero && buyTarget) {
        const heroPassed = hero.getBoundingClientRect().bottom < window.innerHeight * 0.3;
        const purchaseReached = buyTarget.getBoundingClientRect().top < window.innerHeight * 0.75;
        sticky.classList.toggle('is-visible', heroPassed && !purchaseReached);
      }
    };

    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    updateScroll();

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('is-seen');
          });
        }, { threshold: 0.16 })
      : null;
    root.querySelectorAll('.nsl-reveal').forEach((el) => {
      if (observer) observer.observe(el);
      else el.classList.add('is-seen');
    });

    root.querySelectorAll('[data-nsl-scroll-buy]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!buyTarget) return;
        event.preventDefault();
        buyTarget.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });

    /* -------------------------------------------------------- troca de cor */

    const setVariant = (swatch) => {
      swatches.forEach((item) => item.setAttribute('aria-pressed', item === swatch ? 'true' : 'false'));
      activeVariantId = swatch.dataset.variantId || activeVariantId;
      const title = swatch.dataset.title || '';
      const image = swatch.dataset.image || swatch.dataset.fallbackImage || '';
      const price = swatch.dataset.price || '';
      const bg = swatch.dataset.bg || '#e8d7c5';
      root.querySelector('.nsl-colors')?.style.setProperty('--nsl-color-bg', bg);
      if (colorName) colorName.textContent = title;
      if (price) priceNodes.forEach((node) => { node.textContent = price; });
      if (image) {
        productImages.forEach((img) => {
          img.classList.add('is-changing');
          window.setTimeout(() => {
            img.src = image;
            img.alt = `${root.dataset.productTitle || 'Alça'} — ${title}`;
            img.classList.remove('is-changing');
          }, 170);
        });
      }
    };
    swatches.forEach((swatch) => swatch.addEventListener('click', () => setVariant(swatch)));

    /* ------------------------------------------------- montagem do pedido */

    const picker = root.querySelector('[data-nsl-picker]');
    const rows = [...root.querySelectorAll('[data-nsl-row]')];
    const pickerCount = root.querySelector('[data-nsl-picker-count]');
    const pickerTotal = root.querySelector('[data-nsl-picker-total]');
    const submitButton = root.querySelector('[data-nsl-submit]');
    const form = root.querySelector('.nsl-purchase-form');
    const idInput = form?.querySelector('input[name="id"]');

    const rowCount = (row) => Number(row.querySelector('[data-nsl-row-count]')?.textContent || 0);

    // Uma cor sem unidade vira chip; com unidade, vira linha do pedido.
    const setRow = (row, n) => {
      if (row.dataset.available === 'false') return;
      const value = Math.max(0, Math.min(10, n));
      const output = row.querySelector('[data-nsl-row-count]');
      if (output) output.textContent = String(value);
      row.classList.toggle('is-chip', value === 0);
      refreshPicker();
    };

    const refreshPicker = () => {
      if (!picker) return;
      let units = 0;
      let total = 0;
      let firstChosen = null;
      rows.forEach((row) => {
        const n = rowCount(row);
        row.classList.toggle('is-chosen', n > 0);
        if (n > 0) {
          units += n;
          total += n * Number(row.dataset.price || 0);
          if (!firstChosen) firstChosen = row;
        }
      });
      if (pickerCount) pickerCount.textContent = units === 1 ? '1 item' : `${units} itens`;
      if (pickerTotal) pickerTotal.textContent = units ? money(total) : money(0);
      if (submitButton) submitButton.disabled = units === 0;
      // o botão de pagamento rápido compra a cor escolhida
      if (idInput && firstChosen) idInput.value = firstChosen.dataset.variantId;
    };

    rows.forEach((row) => {
      row.dataset.initialQty = String(rowCount(row));
      row.querySelector('[data-nsl-chip]')?.addEventListener('click', () => setRow(row, 1));
      row.querySelectorAll('[data-nsl-row-qty]').forEach((button) => {
        button.addEventListener('click', () => setRow(row, rowCount(row) + Number(button.dataset.nslRowQty || 0)));
      });
    });
    refreshPicker();

    /* --------------------------------------------------------- a gaveta */

    const drawer = root.querySelector('[data-nsl-drawer]');
    const drawerList = root.querySelector('[data-nsl-drawer-list]');
    const drawerEmpty = root.querySelector('[data-nsl-drawer-empty]');
    const drawerTotal = root.querySelector('[data-nsl-drawer-total]');
    const drawerSub = root.querySelector('[data-nsl-drawer-sub]');
    const drawerCheckout = root.querySelector('[data-nsl-drawer-checkout]');
    const cartCount = root.querySelector('[data-nsl-cart-count]');
    const drawerPix = root.querySelector('[data-nsl-drawer-pix]');
    const pixDiscount = Number(root.dataset.pixDiscount || 0);
    const gift = root.querySelector('[data-nsl-gift]');
    const giftText = root.querySelector('[data-nsl-gift-text]');
    const giftBar = root.querySelector('[data-nsl-gift-bar]');
    const giftGoal = Number(root.dataset.giftGoal || 0) * 100;
    const giftLabel = root.dataset.giftLabel || 'um brinde';
    let lastFocus = null;

    const openDrawer = () => {
      if (!drawer) return;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      window.requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.classList.add('nsl-no-scroll');
      drawer.querySelector('[data-nsl-drawer-close]')?.focus({ preventScroll: true });
    };

    const closeDrawer = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('nsl-no-scroll');
      window.setTimeout(() => { drawer.hidden = true; }, 320);
      lastFocus?.focus?.({ preventScroll: true });
    };

    root.querySelectorAll('[data-nsl-drawer-close]').forEach((el) => el.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
    });
    root.querySelectorAll('[data-nsl-cart-open]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        renderCart().then(openDrawer);
      });
    });

    const lineMarkup = (item, index) => {
      const image = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';
      return `
        <li class="nsl-line" data-line="${index + 1}">
          <span class="nsl-line-thumb">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}</span>
          <span class="nsl-line-body">
            <strong>${item.product_title}</strong>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${item.variant_title}</small>` : ''}
            <span class="nsl-line-price">${money(item.final_line_price)}</span>
          </span>
          <span class="nsl-line-actions">
            <span class="nsl-qty nsl-qty--sm">
              <button type="button" data-nsl-line-qty="-1" aria-label="Diminuir">−</button>
              <output>${item.quantity}</output>
              <button type="button" data-nsl-line-qty="1" aria-label="Aumentar">+</button>
            </span>
            <button class="nsl-line-remove" type="button" data-nsl-line-remove>Remover</button>
          </span>
        </li>`;
    };

    const paintCart = (cart) => {
      const items = cart.items || [];
      if (cartCount) {
        cartCount.textContent = String(cart.item_count || 0);
        cartCount.hidden = !cart.item_count;
      }
      if (drawerList) drawerList.innerHTML = items.map(lineMarkup).join('');
      if (drawerEmpty) drawerEmpty.hidden = items.length > 0;
      if (drawerTotal) drawerTotal.textContent = money(cart.total_price || 0);
      if (drawerSub) {
        drawerSub.textContent = !cart.item_count
          ? 'Nenhum item ainda'
          : cart.item_count === 1 ? '1 item' : `${cart.item_count} itens`;
      }
      if (drawerCheckout) drawerCheckout.disabled = !cart.item_count;
      // Barra de progresso do brinde. A regra em si roda no app Dr Free Gift;
      // aqui só mostramos o quanto falta, com o mesmo valor configurado lá.
      if (gift && giftGoal > 0) {
        const total = cart.total_price || 0;
        gift.hidden = total === 0;
        const pct = Math.min(100, Math.round((total / giftGoal) * 100));
        if (giftBar) giftBar.style.width = `${pct}%`;
        gift.classList.toggle('is-complete', total >= giftGoal);
        if (giftText) {
          giftText.textContent = total >= giftGoal
            ? `Você ganhou ${giftLabel}`
            : `Faltam ${money(giftGoal - total)} para ganhar ${giftLabel}`;
        }
      }
      if (drawerPix) {
        const show = pixDiscount > 0 && cart.total_price > 0;
        drawerPix.hidden = !show;
        if (show) {
          const pix = Math.round((cart.total_price * (100 - pixDiscount)) / 100);
          drawerPix.textContent = `${money(pix)} pagando no Pix · ${pixDiscount}% de desconto`;
        }
      }
    };

    const renderCart = async () => {
      try {
        paintCart(await cartFetch('cart.js'));
      } catch (error) {
        /* carrinho indisponível: a gaveta continua utilizável */
      }
    };

    const changeLine = async (line, quantity) => {
      try {
        paintCart(await cartFetch('cart/change.js', { line, quantity }));
      } catch (error) {
        if (message) message.textContent = error.message;
      }
    };

    drawerList?.addEventListener('click', (event) => {
      const row = event.target.closest('[data-line]');
      if (!row) return;
      const line = Number(row.dataset.line);
      const current = Number(row.querySelector('output')?.textContent || 1);
      if (event.target.closest('[data-nsl-line-remove]')) return changeLine(line, 0);
      const step = event.target.closest('[data-nsl-line-qty]');
      if (step) changeLine(line, Math.max(0, current + Number(step.dataset.nslLineQty)));
    });

    drawerCheckout?.addEventListener('click', () => {
      window.location.assign(root.dataset.checkout === 'cart' ? `${shopRoot()}cart` : `${shopRoot()}checkout`);
    });

    /* ------------------------------------------------ adicionar ao carrinho */

    const addChosen = async () => {
      const items = rows
        .map((row) => ({ id: Number(row.dataset.variantId), quantity: rowCount(row) }))
        .filter((item) => item.id && item.quantity > 0);
      if (!items.length) {
        if (message) message.textContent = 'Escolha ao menos uma cor para continuar.';
        return;
      }
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');
      if (message) message.textContent = '';
      try {
        await cartFetch('cart/add.js', { items });
        await renderCart();
        openDrawer();
      } catch (error) {
        if (message) message.textContent = error.message;
      } finally {
        submitButton.classList.remove('is-loading');
        rows.forEach((row) => setRow(row, Number(row.dataset.initialQty || 0)));
      }
    };

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      addChosen();
    });
    if (submitButton && !form) submitButton.addEventListener('click', addChosen);

    // O botão de pagamento rápido é montado pela Shopify. Se a loja não tem
    // carteira ativa, ele vem vazio — nesse caso o bloco inteiro some.
    const express = root.querySelector('[data-nsl-express]');
    if (express) {
      const check = () => {
        const filled = express.querySelector('shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button__button, [data-testid="Checkout-button"]');
        express.hidden = !filled;
      };
      check();
      window.setTimeout(check, 1200);
      window.setTimeout(check, 3500);
    }

    renderCart();
  });
})();
